import React, { useState, useEffect } from "react";
import { IoChevronBack } from "react-icons/io5";
import Slider from "@mui/material/Slider";
import { styled } from "@mui/material/styles";
import { apiBaseUrl } from "../../config";
import axios from "axios";
import { useSelector } from "react-redux";

const PriceSlider = styled(Slider)(({ theme }) => ({}));

const ProductFilter = ({
  products = [],
  initialFilters = {},
  onApply,
  onClose
}) => {
  const [selectedTab, setSelectedTab] = useState("Sort");
  const [selectedCategories, setSelectedCategories] = useState(initialFilters.categories || []);
  const [selectedSubcategories, setSelectedSubcategories] = useState(initialFilters.subcategories || []);
  const [selectedVariants, setSelectedVariants] = useState(initialFilters.variants || []);
  const [selectedAttributes, setSelectedAttributes] = useState(initialFilters.attributes || []);
  const [selectedSort, setSelectedSort] = useState(initialFilters.sort || "");
  const [priceRange, setPriceRange] = useState(initialFilters.priceRange || [0, 1000]);
  const [maxPrice, setMaxPrice] = useState(1000);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [variantGroups, setVariantGroups] = useState([]);
  const [attributeGroups, setAttributeGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const storeId = localStorage.getItem("storeid");
  const userid = useSelector((state) => state.fetchUser?.user?.id);

  const handleTabSelect = (tabName) => setSelectedTab(tabName);

  const handleCategorySelect = (category) => {
    const updated = selectedCategories.some((c) => c._id === category._id)
      ? selectedCategories.filter((c) => c._id !== category._id)
      : [...selectedCategories, category];
  
    setSelectedCategories(updated);
  
    const filtered = products.filter((p) =>
      updated.length === 0 || updated.some((cat) => p.category?.id === cat._id)
    );
  
    updateAttributeGroupsFromFilteredProducts(filtered);
  };  

  const handleSubcategoryChange = (e, subcategory) => {
    let updated;
    if (e.target.checked) {
      updated = [...selectedSubcategories, subcategory];
    } else {
      updated = selectedSubcategories.filter((item) => item !== subcategory);
    }
  
    setSelectedSubcategories(updated);
  
    const filtered = products.filter((p) =>
      updated.length === 0 || updated.some((sub) => p.subcategory?.title === sub)
    );
  
    updateAttributeGroupsFromFilteredProducts(filtered);
  };  

  const handleVariantSelect = (groupTitle, option) => {
    setSelectedVariants((prev) => {
      const groupExists = prev.some((v) => v.groupTitle === groupTitle);
      if (groupExists) {
        return prev
          .map((v) => {
            if (v.groupTitle === groupTitle) {
              const optionExists = v.options.some((o) => o.valueId === option.valueId);
              return {
                ...v,
                options: optionExists
                  ? v.options.filter((o) => o.valueId !== option.valueId)
                  : [...v.options, option],
              };
            }
            return v;
          })
          .filter((v) => v.options.length > 0);
      } else {
        return [...prev, { groupTitle, options: [option] }];
      }
    });
  };

  const handleAttributeSelect = (groupTitle, attributeId, value) => {
    setSelectedAttributes((prev) => {
      const groupExists = prev.some(
        (a) => a.groupTitle === groupTitle && a.attributeId === attributeId
      );
      if (groupExists) {
        return prev
          .map((a) => {
            if (a.groupTitle === groupTitle && a.attributeId === attributeId) {
              const valueExists = a.values.some((v) => v.id === value.id);
              return {
                ...a,
                values: valueExists
                  ? a.values.filter((v) => v.id !== value.id)
                  : [...a.values, value],
              };
            }
            return a;
          })
          .filter((a) => a.values.length > 0);
      } else {
        return [...prev, { groupTitle, attributeId, values: [value] }];
      }
    });
  };

  const handlePriceRangeChange = (newValue) => {
    setPriceRange(newValue);
  };

  const getFilteredSubcategories = () => {
    if (selectedCategories.length === 0) return subcategories;
    return subcategories.filter((sub) =>
      selectedCategories.some((cat) => cat._id === sub.categoryId)
    );
  };

  const handleApply = () => {
    let filtered = products.filter((product) => {
      const price =
      typeof product.price === "number" && product.price > 0
        ? product.price
        : typeof product.mrp === "number"
        ? product.mrp
        : 0;
      const priceInRange = price >= priceRange[0] && price <= priceRange[1];

      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.some(
          (cat) => product.category?.id === cat._id || product.category?.title === cat.title
        );

      const matchesSubcategory =
        selectedSubcategories.length === 0 ||
        selectedSubcategories.some(
          (subcategory) =>
            product.subcategory?.title === subcategory ||
            product.subcategory?.id === subcategory
        );

      const matchesVariants =
        selectedVariants.length === 0 ||
        selectedVariants.every((selectedVariant) => {
          const groupTitle = (selectedVariant.groupTitle || "").trim().toLowerCase();
          const productVariantGroups = product.additionalInfo?.variantGroups || [];

          const productGroup = productVariantGroups.find(
            (group) => (group.title || "").trim().toLowerCase() === groupTitle
          );

          if (!productGroup) return false;

          return selectedVariant.options.some((selectedOption) =>
            (productGroup.options || []).some(
              (productOption) => productOption.valueId === selectedOption.valueId
            )
          );
        });

      const matchesAttributes =
        selectedAttributes.length === 0 ||
        selectedAttributes.every((selectedAttr) => {
          const groupTitle = (selectedAttr.groupTitle || "").trim().toLowerCase();
          const productAttributeGroups = product.additionalInfo?.attributeGroups || [];

          const productGroup = productAttributeGroups.find(
            (group) => group?.title?.trim().toLowerCase() === groupTitle
          );

          if (!productGroup) return false;

          return selectedAttr.values.some((selectedValue) =>
            (productGroup.values || []).some(
              (productValue) => productValue.id === selectedValue.id
            )
          );
        });

      return (
        priceInRange &&
        matchesCategory &&
        matchesSubcategory &&
        matchesVariants &&
        matchesAttributes
      );
    });

    if (selectedSort === "Price: Low to High") {
      filtered.sort((a, b) =>{
        const aval = a.price ? a.price : a.mrp;
        const bval = b.price ? b.price : b.mrp;
        return aval - bval;
      });
    } else if (selectedSort === "Price: High to Low") {
      filtered.sort((a, b) => {
        const aval = a.price ? a.price : a.mrp;
        const bval = b.price ? b.price : b.mrp;
        return bval - aval;
      });
    } else if (selectedSort === "Newest First") {
      filtered?.slice().sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    onApply({
      filteredProducts: filtered,
      filters: {
        categories: selectedCategories,
        subcategories: selectedSubcategories,
        priceRange,
        variants: selectedVariants,
        attributes: selectedAttributes,
        sort: selectedSort,
      }
    });
  };

  const handleClearAll = () => {
    setSelectedCategories([]);
    setSelectedSubcategories([]);
    setSelectedVariants([]);
    setSelectedAttributes([]);
    setSelectedSort("");
    setPriceRange([0, maxPrice]);
  };

  const updateAttributeGroupsFromFilteredProducts = (filteredProducts) => {
    const allAttributes = filteredProducts.flatMap((p) => p.additionalInfo?.attributeGroups || []);
    const attributeGroupMap = {};
  
    allAttributes.forEach((group) => {
      if (!group.title) return;
      if (attributeGroupMap[group.title]) {
        const existingValues = attributeGroupMap[group.title].values;
        (group.values || []).forEach((val) => {
          if (!existingValues.some((v) => v.title === val.title)) {
            existingValues.push(val);
          }
        });
      } else {
        attributeGroupMap[group.title] = {
          title: group.title,
          attributeId: group.attributeId,
          values: group.values ? [...group.values] : [],
        };
      }
    });
  
    setAttributeGroups(Object.values(attributeGroupMap));
  };
  

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.post(
          `${apiBaseUrl}user/filter-seller-products-by-category/${userid}`
        );

        if (response.data.success && response.data.products?.length > 0) {
          const { products } = response.data;

          const categoryMap = new Map();
          const subcategoryMap = new Map();

          products.forEach((product) => {
            if (product.category?.id) {
              categoryMap.set(product.category.id, {
                _id: product.category.id,
                title: product.category.title,
              });
            }
            if (product.subcategory?.id) {
              subcategoryMap.set(product.subcategory.id, {
                _id: product.subcategory.id,
                title: product.subcategory.title,
                categoryId: product.category?.id,
              });
            }
          });

          setCategories(Array.from(categoryMap.values()));
          setSubcategories(Array.from(subcategoryMap.values()));

          const allVariants = products.flatMap((p) => p.additionalInfo?.variantGroups || []);
          const variantGroupMap = {};
          allVariants.forEach((group) => {
            if (!group.title) return;

            if (!variantGroupMap[group.title]) {
              variantGroupMap[group.title] = {
                title: group.title,
                options: [],
              };
            }

            const existingOptions = variantGroupMap[group.title].options;

            (group.options || []).forEach((opt) => {
              const alreadyExists = existingOptions.some((o) => o.valueId === opt.valueId);
              if (!alreadyExists) {
                existingOptions.push(opt);
              }
            });
          });

          setVariantGroups(Object.values(variantGroupMap));

          const allAttributes = products.flatMap((p) => p.additionalInfo?.attributeGroups || []);
          const attributeGroupMap = {};

          allAttributes.forEach((group) => {
            if (!group.title) return;
            if (attributeGroupMap[group.title]) {
              const existingValues = attributeGroupMap[group.title].values;
              (group.values || []).forEach((val) => {
                if (!existingValues.some((v) => v.title === val.title)) {
                  existingValues.push(val);
                }
              });
            } else {
              attributeGroupMap[group.title] = {
                title: group.title,
                attributeId: group.attributeId,
                values: group.values ? [...group.values] : [],
              };
            }
          });

          setAttributeGroups(Object.values(attributeGroupMap));
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFilterData();
  }, [userid]);

  useEffect(() => {
    if (products?.length > 0) {
      const max = Math.max(
        ...products.map((p) =>
          typeof p.price === "number" && p.price > 0
            ? p.price
            : typeof p.mrp === "number"
            ? p.mrp
            : 0
        )
      );      
      const calculatedMax = Math.ceil(max / 100) * 100;
      setMaxPrice(calculatedMax);
      setPriceRange(initialFilters.priceRange || [0, calculatedMax]);
    }
  }, [products, initialFilters.priceRange]);

  const tabs = [
    { name: "Sort" },
    { name: "Category" },
    { name: "Sub Category" },
    { name: "Price Range" },
    ...variantGroups.map((group) => ({ name: group.title, type: "variant" })),
    ...attributeGroups.map((group) => ({ name: group.title, type: "attribute" }))
  ];

  const renderFilterContent = () => {
    if (isLoading) return <div className="loading">Loading...</div>;

    switch (selectedTab) {
      case "Sort":
        return (
          <>
            <label className="filter-option">
              <input
                type="radio"
                name="sort"
                checked={selectedSort === "Price: Low to High"}
                onChange={() => setSelectedSort("Price: Low to High")}
              />
              Price: Low to High
            </label>
            <label className="filter-option">
              <input
                type="radio"
                name="sort"
                checked={selectedSort === "Price: High to Low"}
                onChange={() => setSelectedSort("Price: High to Low")}
              />
              Price: High to Low
            </label>
            <label className="filter-option">
              <input
                type="radio"
                name="sort"
                checked={selectedSort === "Newest First"}
                onChange={() => setSelectedSort("Newest First")}
              />
              Newest First
            </label>
          </>
        );

      case "Category":
        return (
          <div className="category-options">
            {categories.map((category) => (
              <label key={category._id} className="filter-option">
                <input
                  type="checkbox"
                  checked={selectedCategories.some((c) => c._id === category._id)}
                  onChange={() => handleCategorySelect(category)}
                  style={{ width: "25px", height: "25px" }}
                />
                {category.title}
              </label>
            ))}
          </div>
        );

      case "Sub Category":
        return (
          <div className="subcategory-options">
            {getFilteredSubcategories().map((subcategory) => (
              <label key={subcategory._id} className="filter-option">
                <input
                  type="checkbox"
                  checked={selectedSubcategories.includes(subcategory.title)}
                  onChange={(e) => handleSubcategoryChange(e, subcategory.title)}
                  style={{ width: "25px", height: "25px" }}
                />
                {subcategory.title}
              </label>
            ))}
          </div>
        );

      case "Price Range":
        return (
          <div style={{ padding: "20px" }}>
            <PriceSlider
              value={priceRange}
              onChange={(_, newValue) => handlePriceRangeChange(newValue)}
              valueLabelDisplay="on"
              min={0}
              max={maxPrice}
              step={10}
              valueLabelFormat={(value) => `${value}`}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "10px" }}>
              <span>{priceRange[0]}</span>
              <span>{priceRange[1]}</span>
            </div>
          </div>
        );

      default:
        const variantGroup = variantGroups.find((group) => group.title === selectedTab);
        if (variantGroup) {
          return (
            <div className="variant-options">
              {variantGroup.options.map((option, index) => {
                const isSelected = selectedVariants.some(
                  (v) =>
                    v.groupTitle === variantGroup.title &&
                    v.options.some((o) => o.valueId === option.valueId)
                );
                return (
                  <label key={index} className="filter-option">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleVariantSelect(variantGroup.title, option)}
                      style={{ width: "25px", height: "25px" }}
                    />
                    {variantGroup.title === "Color" ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: option.valueTitle.split(/\s*\|\|\s*/)[1]?.trim(),
                            marginRight: "8px",
                            border: "1px solid #ccc"
                          }}
                        />
                        {option.valueTitle.split("||")[0].trim()}
                      </div>
                    ) : (
                      option.valueTitle
                    )}
                  </label>
                );
              })}
            </div>
          );
        }

        const attributeGroup = attributeGroups.find((group) => group.title === selectedTab);
        if (attributeGroup) {
          return (
            <div className="attribute-options">
              {attributeGroup.values.map((value, index) => {
                const isSelected = selectedAttributes.some(
                  (a) =>
                    a.groupTitle === attributeGroup.title &&
                    a.attributeId === attributeGroup.attributeId &&
                    a.values.some((v) => v.id === value.id)
                );
                return (
                  <label key={index} className="filter-option">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() =>
                        handleAttributeSelect(attributeGroup.title, attributeGroup.attributeId, value)
                      }
                      style={{ width: "25px", height: "25px" }}
                    />
                    {value.title}
                  </label>
                );
              })}
            </div>
          );
        }

        return null;
    }
  };

  return (
    <div className="filter-fullscreen">
      <div className="filter-header">
        <h4 className="icon-container" onClick={onClose}>
          <IoChevronBack />
        </h4>
        <h4 className="filter-title">Sort & Filter</h4>
        <h4 className="clear-all" onClick={handleClearAll}>
          CLEAR ALL
        </h4>
      </div>

      <div className="filter-body">
        <div className="filter-container">
          <div className="filter-sidebar">
            {tabs.map((tab, index) => (
              <div
                key={index}
                className={`filter-tab ${selectedTab === tab.name ? "active" : ""}`}
                onClick={() => handleTabSelect(tab.name)}
              >
                {tab.name}
              </div>
            ))}
          </div>

          <div className="filter-content">{renderFilterContent()}</div>
        </div>
      </div>

      <div className="filter-footer">
        <p>Total Products: {products.length}</p>
        <button className="apply-btn" onClick={handleApply}>
          Apply
        </button>
      </div>
    </div>
  );
};

export default ProductFilter;
